"use client";

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./PasswordUpdate.css";
import logo from "../../assets/DHMS_logo.webp";
import Reset from "../../assets/Reset.webp";
import AuthService from "../../Services/authService";
import Toast from "../../Components/Modals/Toast/Toast";

// Simple loading spinner component
const LoadingSpinner = () => (
  <div className="spinner">
    <div className="spinner-circle"></div>
  </div>
);

const PasswordUpdate = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetId, setResetId] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [isChangePasswordMode, setIsChangePasswordMode] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  const location = useLocation();
  const navigate = useNavigate();

  // Extract reset token/ID from URL parameters or location state
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get("token");
    const idFromUrl = urlParams.get("id");
    const resetIdFromUrl = urlParams.get("resetId");

    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
    }

    if (idFromUrl || resetIdFromUrl) {
      setResetId(idFromUrl || resetIdFromUrl);
    }

    const emailFromState = location.state?.email;
    if (emailFromState) {
      setResetEmail(emailFromState);
    }

    // If no reset context but user is authenticated → change-password mode
    if (!tokenFromUrl && !idFromUrl && !resetIdFromUrl && !emailFromState) {
      if (AuthService.isAuthenticated()) {
        setIsChangePasswordMode(true);
      } else {
        showToast(
          "Invalid reset link. Please request a new password reset.",
          "error"
        );
        setTimeout(() => {
          navigate("/forgotPassword");
        }, 3000);
      }
    }
  }, [location, navigate]);

  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isChangePasswordMode && !oldPassword) {
      showToast("Please enter your current password", "error");
      return;
    }

    if (!newPassword) {
      showToast("Please enter a new password", "error");
      return;
    }

    if (newPassword.length < 8) {
      showToast("Password must be at least 8 characters long!", "error");
      return;
    }

    if (!hasUppercase(newPassword)) {
      showToast("Password must contain at least one uppercase letter!", "error");
      return;
    }

    if (!hasNumber(newPassword)) {
      showToast("Password must contain at least one number!", "error");
      return;
    }

    if (!hasSpecialChar(newPassword)) {
      showToast("Password must contain at least one special character!", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast("Passwords do not match!", "error");
      return;
    }

    setIsLoading(true);

    try {
      if (isChangePasswordMode) {
        // Authenticated user changing their password — PUT /updatepassword
        await AuthService.updatePassword({ oldPassword, newPassword });
      } else if (resetId || resetToken) {
        // Flow: user clicked email link with an ID param
        await AuthService.resetPasswordWithId({
          id: resetId || resetToken,
          newPassword,
        });
      } else if (resetEmail) {
        // Flow: user came from forgot-password form (email only, no link ID)
        await AuthService.resetPasswordWithNotification({
          emailaddress: resetEmail,
          newPassword,
        });
      } else {
        showToast("Invalid reset session. Please request a new password reset.", "error");
        setTimeout(() => navigate("/forgotPassword"), 3000);
        return;
      }

      showToast("Password updated successfully!", "success");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Password update failed:", error);

      let errorMessage = "Failed to update password. Please try again.";

      if (error.message.includes("Network error")) {
        errorMessage = "Network error. Please check your internet connection and try again.";
      } else if (error.message.includes("Server error")) {
        errorMessage = "Server is currently unavailable. Please try again in a few minutes.";
      } else if (
        error.message.includes("expired") ||
        error.message.includes("invalid")
      ) {
        errorMessage = isChangePasswordMode
          ? error.message
          : "Reset link has expired or is invalid. Please request a new password reset.";
        if (!isChangePasswordMode) {
          setTimeout(() => navigate("/password-reset"), 3000);
        }
      } else if (error.message.includes("incorrect") || error.message.includes("wrong")) {
        errorMessage = "Current password is incorrect.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleLoginRedirect = () => {
    setShowSuccessModal(false);
    navigate(isChangePasswordMode ? "/dashboard" : "/login");
  };

  const toggleOldPasswordVisibility = () => setShowOldPassword(!showOldPassword);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  // Password validation functions
  const hasMinLength = (password) => password.length >= 8;
  const hasUppercase = (password) => /[A-Z]/.test(password);
  const hasNumber = (password) => /\d/.test(password);
  const hasSpecialChar = (password) => /[@#%&*!]/.test(password);

  return (
    <>
      <div className="password-update-container">
        <div className="content-wrapper">
          <div className="image-section">
            <div className="header-logo">
              <a href="/home">
                <img
                  src={logo || "/placeholder.svg"}
                  alt="IT Service Desk Africa"
                  className="company-logo"
                />
              </a>
            </div>
            <div className="family-image-container">
              <img
                src={Reset || "/placeholder.svg"}
                alt="Happy African family using laptop together"
                className="family-image2"
              />
            </div>
          </div>
          <div className="form-section">
            <div className="form-container">
              <h1 className="form-title">
                {isChangePasswordMode ? "Change Password" : "Reset Password?"}
              </h1>
              <p className="form-subtitle">
                {isChangePasswordMode
                  ? "Enter your current password and choose a new one"
                  : "Choose a new password that is strong and unique"}
              </p>
              <form onSubmit={handleSubmit} className="update-form">
                {isChangePasswordMode && (
                  <div className="input-group">
                    <label htmlFor="oldPassword" className="input-label">
                      Current Password
                    </label>
                    <div className="password-input-container">
                      <input
                        type={showOldPassword ? "text" : "password"}
                        id="oldPassword"
                        className="password-input"
                        placeholder="Enter your current password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        disabled={isLoading}
                        required={isChangePasswordMode}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={toggleOldPasswordVisibility}
                        disabled={isLoading}
                      >
                        {showOldPassword ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>
                )}
                <div className="input-group">
                  <label htmlFor="newPassword" className="input-label">
                    New Password
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="newPassword"
                      className="password-input"
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={togglePasswordVisibility}
                      disabled={isLoading}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="confirmPassword" className="input-label">
                    Confirm Password
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      className="password-input"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleConfirmPasswordVisibility}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                {/* Password Requirements - Only show when typing */}
                {newPassword.length > 0 && (
                  <div className="password-requirements">
                    <p className="requirements-title">
                      Your password must include at least
                    </p>
                    <div className="requirement-list">
                      <div
                        className={`requirement-item ${
                          hasMinLength(newPassword) ? "valid" : "invalid"
                        }`}
                      >
                        <div className="requirement-icon">
                          {hasMinLength(newPassword) ? "✅" : "⭕"}
                        </div>
                        <span className="requirement-text">
                          Minimum of 8 characters
                        </span>
                      </div>
                      <div
                        className={`requirement-item ${
                          hasUppercase(newPassword) ? "valid" : "invalid"
                        }`}
                      >
                        <div className="requirement-icon">
                          {hasUppercase(newPassword) ? "✅" : "⭕"}
                        </div>
                        <span className="requirement-text">
                          One UPPERCASE letter
                        </span>
                      </div>
                      <div
                        className={`requirement-item ${
                          hasNumber(newPassword) ? "valid" : "invalid"
                        }`}
                      >
                        <div className="requirement-icon">
                          {hasNumber(newPassword) ? "✅" : "⭕"}
                        </div>
                        <span className="requirement-text">one number</span>
                      </div>
                      <div
                        className={`requirement-item ${
                          hasSpecialChar(newPassword) ? "valid" : "invalid"
                        }`}
                      >
                        <div className="requirement-icon">
                          {hasSpecialChar(newPassword) ? "✅" : "⭕"}
                        </div>
                        <span className="requirement-text">
                          One special character (e.g "@#%*&?")
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="update-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner />
                      Updating Password...
                    </>
                  ) : (
                    "Update password"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                ✕
              </button>
              <div className="modal-body">
                <div className="success-icon">✅</div>
                <h2 className="modal-title">
                  {isChangePasswordMode ? "Password Updated" : "Password Reset"}
                </h2>
                <p className="modal-description">
                  {isChangePasswordMode
                    ? "Your password has been successfully updated."
                    : "Your password has been successfully reset. Please log in with your new password."}
                </p>
                <button
                  className="modal-login-btn"
                  onClick={handleLoginRedirect}
                >
                  {isChangePasswordMode ? "Go to Dashboard" : "Log in"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
        duration={5000}
      />
    </>
  );
};

export default PasswordUpdate;
